import { observer } from 'mobx-react';
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Tooltip
} from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Select from 'react-select';
import { useStores } from '../stores/useStores';

function TrappingZoneSprayForm({
  handleSubmit,
  handleChange,
  handleBlur,
  values,
  errors,
  setFieldValue,
  selectTrappingZones,
  coverageDays
}) {
  const { traplogStore } = useStores();
  const [prevCoverageDays, setPrevCoverageDays] = React.useState(
    values.coverage_days
  );
  const selectedTrappingZone = traplogStore.selectedTrappingZone;
  const [selectedOptions, setSelectedOptions] = useState(
    selectedTrappingZone
      ? [
          {
            label: selectedTrappingZone.name,
            value: selectedTrappingZone.id
          }
        ]
      : []
  );
  const [allSelected, setAllSelected] = useState(false);
  const selectRef = useRef(null);

  const options = traplogStore.trappingZones.map((trappingZone) => {
    return {
      value: trappingZone.id,
      label: trappingZone.name
    };
  });
  const selectedInsectGroupId = traplogStore.selectedInsectGroupId;
  const biofixInsectGroupIds = [1]; // Codling-moth.
  const displayBiofix = biofixInsectGroupIds.includes(selectedInsectGroupId);
  // Filter products by the products insect group and selected insect group.
  const filteredSprayProducts = useMemo(
    () =>
      traplogStore.sprayProducts.filter((product) => {
        const hasInsect = product.insects.find(
          (insect) =>
            insect.insect_group_id === traplogStore.selectedInsectGroupId
        );
        return hasInsect;
      }),
    [traplogStore.sprayProducts]
  );

  const handleSelectAll = () => {
    const newOptions = !allSelected ? options : [];
    selectRef.current.selectOption(newOptions);
    setFieldValue('trapping_zones', newOptions);
    setSelectedOptions(newOptions);
    setAllSelected(!allSelected);
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {/* Select Production Sites */}
      {selectTrappingZones && (
        <Form.Group controlId='trapping-zones'>
          <Form.Label>Select Production Sites</Form.Label>
          <Row>
            <Col xs={7} sm={8} md={8}>
              <Select
                ref={selectRef}
                isMulti
                options={options}
                isLoading={traplogStore.loadingTrappingZones}
                value={selectedOptions}
                defaultValue={
                  selectedTrappingZone
                    ? {
                        label: selectedTrappingZone.name,
                        value: selectedTrappingZone.id
                      }
                    : null
                }
                onChange={(options) => {
                  setFieldValue('trapping_zones', options);
                  setSelectedOptions(options);
                }}
                onBlur={() => {
                  handleBlur({ target: { name: 'trapping_zones' } });
                }}
              />
            </Col>
            <Col className='text-center' xs={5} sm={4} md={4}>
              <Button
                variant='success'
                onClick={() => {
                  handleSelectAll();
                }}
              >
                {!allSelected ? 'Select All' : 'Unselect All'}
              </Button>
            </Col>
          </Row>
          {errors.trapping_zones ? (
            <div className='text-danger' style={{ fontSize: '80%' }}>
              {errors.trapping_zones}
            </div>
          ) : null}
        </Form.Group>
      )}
      {/* Spray Product */}
      <Form.Group controlId='sprayProduct'>
        <Form.Label>Spray Product</Form.Label>
        <Select
          options={filteredSprayProducts.map((sprayProduct) => {
            return {
              value: sprayProduct.id,
              label: sprayProduct.product.name
            };
          })}
          isLoading={traplogStore.loadingSprayProducts}
          defaultValue={values.sprayProduct}
          onChange={(option) => setFieldValue('sprayProduct', option)}
          onBlur={() => {
            handleBlur({ target: { name: 'sprayProduct' } });
          }}
        />
        {errors.sprayProduct ? (
          <div className='text-danger' style={{ fontSize: '80%' }}>
            {errors.sprayProduct}
          </div>
        ) : null}
      </Form.Group>

      {/* Applied Date */}
      <Form.Group controlId='applied_date'>
        <Form.Label>Applied Date</Form.Label>
        <Form.Control
          type='date'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.applied_date}
          isInvalid={errors.applied_date}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.applied_date}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Spray Coverage */}
      {coverageDays && (
        <Form.Group controlId='coverage_days'>
          <Form.Label>Spray Coverage</Form.Label>
          <InputGroup>
            <Form.Control
              type='number'
              min='1'
              step='1'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.coverage_days}
              isInvalid={errors.coverage_days}
            />
            <InputGroup.Append>
              <InputGroup.Text>Days</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Form.Group>
      )}

      {/* Comments */}
      <Form.Group controlId='comments'>
        <Form.Label>Comments</Form.Label>
        <Form.Control
          as='textarea'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.comments}
        />
      </Form.Group>

      {/* Biofix */}
      {displayBiofix && (
        <Form.Group controlId='biofix'>
          <OverlayTrigger
            placement='right'
            overlay={
              <Tooltip>
                A biofix spray affects 2 weeks and appears before any
                monitoring.
              </Tooltip>
            }
          >
            <div style={{ width: '30%' }}>
              <Form.Check
                id='biofix'
                type='checkbox'
                label='Biofix Spray?'
                onChange={(event) => {
                  // Set coverage days to 14 when biofix checked
                  // Also, keep track of previous coverage days so it
                  // can be reverted when biofix is unchecked.
                  if (event.target.checked) {
                    setPrevCoverageDays(values.coverage_days);
                    setFieldValue('coverage_days', 14);
                  } else {
                    setFieldValue('coverage_days', prevCoverageDays);
                  }
                  setFieldValue('biofix', !values.biofix);
                }}
                onBlur={handleBlur}
                defaultChecked={values.biofix}
                style={{ zIndex: 0 }}
                custom
              />
            </div>
          </OverlayTrigger>
        </Form.Group>
      )}
      {errors?.external?.existingBiofix ? (
        <div>
          <Alert variant='danger'>{errors.external.existingBiofix}</Alert>
        </div>
      ) : null}
    </Form>
  );
}

export default observer(TrappingZoneSprayForm);
